import { useEffect, useMemo, useState } from "react";

const emptyForm = {
  id: null,
  slug: "",
  title: "",
  meta_description: "",
  h1: "",
  excerpt: "",
  body: "",
  keywords: "",
  cta_label: "Book a Consultation",
  cta_url: "/#contact",
  status: "scheduled",
  publish_at: "",
  canonical_url: "",
  og_image: "/logo.png",
  author: "Lion Pride Financial",
};

function toDatetimeLocal(value) {
  if (!value) return "";
  const normalized = String(value).replace(" ", "T");
  return normalized.slice(0, 16);
}

function fromDatetimeLocal(value) {
  return value ? value.replace("T", " ") + ":00" : "";
}

function cleanSlug(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\-/]+/g, "-")
    .replace(/\/+/g, "/")
    .replace(/^-+|-+$/g, "")
    .replace(/\/-+|-+\//g, "/")
    .replace(/^\/+|\/+$/g, "");
}

function countWords(text) {
  return String(text || "")
    .replace(/<[^>]+>/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
}

function scoreSeo(form) {
  const titleLen = form.title.length;
  const descLen = form.meta_description.length;
  const bodyWords = countWords(form.body);
  const checks = [
    { label: "Slug is clean and specific", ok: !!cleanSlug(form.slug) && cleanSlug(form.slug).length >= 6 },
    { label: "SEO title is 45–65 characters", ok: titleLen >= 45 && titleLen <= 65 },
    { label: "Meta description is 120–160 characters", ok: descLen >= 120 && descLen <= 160 },
    { label: "H1 is present and aligned with the title", ok: form.h1.trim().length >= 15 },
    { label: "Body has at least 300 words", ok: bodyWords >= 300 },
    { label: "CTA is present", ok: !!form.cta_label.trim() && !!form.cta_url.trim() },
    { label: "Future publish time set", ok: form.status !== "scheduled" || !!form.publish_at },
    { label: "OpenGraph image set", ok: !!form.og_image.trim() },
  ];
  const passed = checks.filter((c) => c.ok).length;
  return { checks, passed, total: checks.length, percent: Math.round((passed / checks.length) * 100), bodyWords };
}

export default function ScheduledSeoPagesWidget() {
  const [pages, setPages] = useState([]);
  const [baseUrl, setBaseUrl] = useState("https://lionpridefinancial.com");
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const seo = useMemo(() => scoreSeo(form), [form]);

  const loadPages = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/page_admin.php?action=list");
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Unable to load pages");
      setPages(data.pages || []);
      if (data.base_url) setBaseUrl(data.base_url);
    } catch (err) {
      setMessage(err.message || "Unable to load scheduled pages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPages();
  }, []);

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: key === "slug" ? cleanSlug(value) : value }));
  };

  const editPage = (page) => {
    setForm({
      ...emptyForm,
      ...page,
      publish_at: toDatetimeLocal(page.publish_at),
    });
    setMessage(`Editing ${page.slug}`);
  };

  const resetForm = () => {
    setForm(emptyForm);
    setMessage("");
  };

  const savePage = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    try {
      const payload = { ...form, slug: cleanSlug(form.slug), publish_at: fromDatetimeLocal(form.publish_at) };
      const res = await fetch("/api/page_admin.php?action=save", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "save", ...payload }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || data.detail || "Save failed");
      setMessage("Page saved successfully.");
      resetForm();
      await loadPages();
    } catch (err) {
      setMessage(err.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const publishNow = async (id) => {
    if (!window.confirm("Publish this page now?")) return;
    setLoading(true);
    try {
      const res = await fetch("/api/page_admin.php?action=publish_now", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "publish_now", id }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || data.detail || "Publish failed");
      setMessage("Page published.");
      await loadPages();
    } catch (err) {
      setMessage(err.message || "Publish failed");
    } finally {
      setLoading(false);
    }
  };

  const deletePage = async (id) => {
    if (!window.confirm("Delete this scheduled page from the admin database? This does not remove an already-published HTML file.")) return;
    setLoading(true);
    try {
      const res = await fetch("/api/page_admin.php?action=delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "delete", id }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Delete failed");
      setMessage("Page deleted.");
      await loadPages();
    } catch (err) {
      setMessage(err.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const previewUrl = form.slug ? `${baseUrl}/${cleanSlug(form.slug)}/` : "";

  return (
    <div className="bg-white text-gray-900 rounded-2xl shadow p-6 w-full">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-5">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Scheduled SEO Page Publisher</h2>
          <p className="text-sm text-gray-600 mt-1">
            Create a static SEO page, choose its slug, and schedule it to publish automatically.
          </p>
        </div>
        <div className="text-sm bg-gray-100 border rounded-xl px-4 py-3 min-w-[160px]">
          <div className="font-semibold">SEO Score</div>
          <div className="text-2xl font-bold text-blue-700">{seo.percent}%</div>
          <div className="text-xs text-gray-600">{seo.passed}/{seo.total} checks · {seo.bodyWords} words</div>
        </div>
      </div>

      {message && (
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          {message}
        </div>
      )}

      <form onSubmit={savePage} className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm font-medium">Slug</span>
              <input className="mt-1 w-full border rounded-lg px-3 py-2" value={form.slug} onChange={(e) => update("slug", e.target.value)} placeholder="cash-balance-plan-business-owners" required />
              {previewUrl && <span className="block mt-1 text-xs text-gray-500">{previewUrl}</span>}
            </label>
            <label className="block">
              <span className="text-sm font-medium">Status</span>
              <select className="mt-1 w-full border rounded-lg px-3 py-2" value={form.status} onChange={(e) => update("status", e.target.value)}>
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="published">Publish on save</option>
              </select>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm font-medium">Publish date/time</span>
              <input type="datetime-local" className="mt-1 w-full border rounded-lg px-3 py-2" value={form.publish_at} onChange={(e) => update("publish_at", e.target.value)} />
            </label>
            <label className="block">
              <span className="text-sm font-medium">OpenGraph image</span>
              <input className="mt-1 w-full border rounded-lg px-3 py-2" value={form.og_image} onChange={(e) => update("og_image", e.target.value)} placeholder="/logo.png" />
            </label>
          </div>

          <label className="block">
            <span className="text-sm font-medium">SEO Title</span>
            <input className="mt-1 w-full border rounded-lg px-3 py-2" value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="Cash Balance Plans for Business Owners | Lion Pride Financial" required />
            <span className="text-xs text-gray-500">{form.title.length} characters. Aim for 45–65.</span>
          </label>

          <label className="block">
            <span className="text-sm font-medium">Meta Description</span>
            <textarea className="mt-1 w-full border rounded-lg px-3 py-2" rows="2" value={form.meta_description} onChange={(e) => update("meta_description", e.target.value)} placeholder="Learn how business owners use cash balance plans to support tax-efficient retirement planning, liquidity, and long-term exit strategy." required />
            <span className="text-xs text-gray-500">{form.meta_description.length} characters. Aim for 120–160.</span>
          </label>

          <label className="block">
            <span className="text-sm font-medium">H1</span>
            <input className="mt-1 w-full border rounded-lg px-3 py-2" value={form.h1} onChange={(e) => update("h1", e.target.value)} placeholder="Cash Balance Plans for Business Owners" required />
          </label>

          <label className="block">
            <span className="text-sm font-medium">Excerpt / Hero Summary</span>
            <textarea className="mt-1 w-full border rounded-lg px-3 py-2" rows="2" value={form.excerpt} onChange={(e) => update("excerpt", e.target.value)} placeholder="A short summary that appears at the top of the page." />
          </label>

          <label className="block">
            <span className="text-sm font-medium">Body</span>
            <textarea className="mt-1 w-full border rounded-lg px-3 py-2 font-mono text-sm" rows="14" value={form.body} onChange={(e) => update("body", e.target.value)} placeholder={"Use plain text paragraphs or simple HTML like <h2>, <p>, <ul>, <li>.\n\nKeep claims balanced and avoid guarantees."} required />
            <span className="text-xs text-gray-500">{seo.bodyWords} words. Aim for 300+ for a focused SEO page.</span>
          </label>
        </div>

        <div className="space-y-4">
          <div className="border rounded-xl p-4 bg-gray-50">
            <h3 className="font-semibold mb-3">SEO Checklist</h3>
            <ul className="space-y-2 text-sm">
              {seo.checks.map((check) => (
                <li key={check.label} className={check.ok ? "text-green-700" : "text-gray-600"}>
                  {check.ok ? "✓" : "○"} {check.label}
                </li>
              ))}
            </ul>
          </div>

          <label className="block">
            <span className="text-sm font-medium">Keywords</span>
            <input className="mt-1 w-full border rounded-lg px-3 py-2" value={form.keywords} onChange={(e) => update("keywords", e.target.value)} placeholder="cash balance plan, business owner tax planning" />
          </label>

          <label className="block">
            <span className="text-sm font-medium">CTA Label</span>
            <input className="mt-1 w-full border rounded-lg px-3 py-2" value={form.cta_label} onChange={(e) => update("cta_label", e.target.value)} />
          </label>

          <label className="block">
            <span className="text-sm font-medium">CTA URL</span>
            <input className="mt-1 w-full border rounded-lg px-3 py-2" value={form.cta_url} onChange={(e) => update("cta_url", e.target.value)} />
          </label>

          <label className="block">
            <span className="text-sm font-medium">Canonical URL, optional</span>
            <input className="mt-1 w-full border rounded-lg px-3 py-2" value={form.canonical_url || ""} onChange={(e) => update("canonical_url", e.target.value)} placeholder="auto-generated if blank" />
          </label>

          <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-900">
            <strong>Compliance reminder:</strong> keep financial/insurance claims balanced. Avoid guarantees, tax promises, or exaggerated return language.
          </div>

          <div className="flex gap-2">
            <button disabled={loading} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-4 py-2 rounded-lg" type="submit">
              {form.id ? "Update Page" : "Save Page"}
            </button>
            <button type="button" onClick={resetForm} className="border px-4 py-2 rounded-lg">
              Clear
            </button>
          </div>
        </div>
      </form>

      <div className="mt-8">
        <h3 className="font-semibold mb-3">Scheduled / Published Pages</h3>
        <div className="overflow-x-auto border rounded-xl">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="text-left p-3">Slug</th>
                <th className="text-left p-3">Title</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Publish At</th>
                <th className="text-left p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pages.map((page) => (
                <tr key={page.id} className="border-t">
                  <td className="p-3 font-mono text-xs">/{page.slug}/</td>
                  <td className="p-3">{page.title}</td>
                  <td className="p-3"><span className="rounded bg-gray-100 px-2 py-1">{page.status}</span></td>
                  <td className="p-3">{page.publish_at || "—"}</td>
                  <td className="p-3 space-x-2 whitespace-nowrap">
                    <button className="text-blue-700 hover:underline" onClick={() => editPage(page)}>Edit</button>
                    {page.status === "published" && <a className="text-green-700 hover:underline" href={`/${page.slug}/`} target="_blank" rel="noreferrer">View</a>}
                    {page.status !== "published" && <button className="text-green-700 hover:underline" onClick={() => publishNow(page.id)}>Publish now</button>}
                    <button className="text-red-700 hover:underline" onClick={() => deletePage(page.id)}>Delete</button>
                  </td>
                </tr>
              ))}
              {!pages.length && (
                <tr><td className="p-4 text-gray-500" colSpan="5">No scheduled pages yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
